import { loadLayerStack, type LayerStackAsset, type R2Index } from '../../layerstack/LayerStackLoader';

/**
 * Which authored asset every simulation entity draws as, and how the set of
 * stacks the game needs is assembled for preload. Slug tables only: the sprites
 * built from them belong to the family that draws them.
 */
const RT_RUNG = 'RT';

export const PLAYER_SLUG = 'veil-skimmer';
export const NODE_SLUG = 'prism-node';
export const PICKUP_SLUG = 'pickup';
export const GATE_SLUG = 'calibration-gate';

/**
 * Indexed by EnemyType, NOT by slug number: the r2 slugs were numbered in
 * sheet order (23 = LOCK BEETLE) while EnemyType is declared in gameplay order
 * (5 = LockBeetle). Matching by name is the only correct join.
 */
export const ENEMY_SLUGS: readonly string[] = Object.freeze([
  '20-threadling',
  '21-plate-fin',
  '22-indexer',
  '26-shade-polyp',
  '25-mirror-wisp',
  '23-lock-beetle',
  '27-fold-ray',
  '24-archive-ram',
  '28-choir-engine',
  '29-prism-eater',
]);

/** Indexed by BossKind; BossKind.None has no art. */
export const BOSS_SLUGS: readonly string[] = Object.freeze([
  '',
  '30-catalogue-mouth',
  '31-null-scribe',
  '32-mnemosyne-array',
]);

/**
 * Indexed by ProjectileKind. '' means the kind is resolved elsewhere (Node
 * follows the equipped lens) or has no authored glyph and falls back to a quad.
 */
export const PROJECTILE_SLUGS: readonly string[] = Object.freeze([
  'pulse',
  'arc',
  'prism',
  'lattice',
  '',
  'ordinary-bullet',
  'piercing-bullet',
  'laser',
  'node-lock-shot',
  'mirror-reflection',
  '',
]);

/** Indexed by NodeLensId, for ProjectileKind.Node. */
export const NODE_LENS_SLUGS: readonly string[] = Object.freeze([
  'focus',
  'fan',
  'mirror-reflection',
  'tether-active',
]);

/** The RT rung path for a slug, or a build instruction if the index lacks it. */
export function rtPath(index: R2Index, slug: string): string {
  const asset = index.assets.find((candidate) => candidate.slug === slug);
  if (!asset) throw new Error(`r2 index has no asset "${slug}" — run: bun run assets:r2`);
  const rung = asset.rungs.find((candidate) => candidate.rung === RT_RUNG);
  if (!rung) throw new Error(`r2 asset "${slug}" has no ${RT_RUNG} rung — run: bun run assets:r2`);
  return rung.path;
}

/** Every entity stack the game can draw, deduplicated, in preload order. */
export function entityStackSlugs(): string[] {
  const slugs: string[] = [PLAYER_SLUG, NODE_SLUG, PICKUP_SLUG, GATE_SLUG];
  for (const slug of ENEMY_SLUGS) slugs.push(slug);
  for (const slug of BOSS_SLUGS) if (slug !== '') slugs.push(slug);
  for (const slug of PROJECTILE_SLUGS) if (slug !== '' && !slugs.includes(slug)) slugs.push(slug);
  for (const slug of NODE_LENS_SLUGS) if (!slugs.includes(slug)) slugs.push(slug);
  return slugs;
}

export function loadEntityStacks(index: R2Index, slugs: readonly string[]): Promise<LayerStackAsset[]> {
  return Promise.all(slugs.map((slug) => loadLayerStack(rtPath(index, slug))));
}
